import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LogDto, SupplierDetailDto, SupplierDto } from "@audit-plan/shared";
import { api } from "./client";

export interface SupplierInput {
  code: string;
  name: string;
  vat?: string;
  country?: string;
  legalAddress?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  category?: string;
  sector?: string;
  risk: "low" | "medium" | "high";
}

export function useSuppliers(q?: { q?: string; risk?: string; category?: string; phaseOut?: boolean; includeInactive?: boolean }) {
  return useQuery({
    queryKey: ["suppliers", q ?? {}],
    queryFn: async () => {
      const r = await api.get<SupplierDto[] | { items: SupplierDto[] }>("/suppliers", q);
      return Array.isArray(r) ? r : r.items;
    },
  });
}

export function useSupplier(id?: string) {
  return useQuery({ queryKey: ["supplier", id], queryFn: () => api.get<SupplierDetailDto>(`/suppliers/${id}`), enabled: !!id });
}

export function useSupplierLog(id?: string, enabled = true) {
  return useQuery({
    queryKey: ["log", { supplierId: id }],
    queryFn: async () => {
      const r = await api.get<LogDto[] | { items: LogDto[] }>("/log", { supplierId: id, limit: 100 });
      return Array.isArray(r) ? r : r.items;
    },
    enabled: !!id && enabled,
  });
}

export function useSaveSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: SupplierInput }) =>
      id ? api.patch<SupplierDto>(`/suppliers/${id}`, body) : api.post<SupplierDto>("/suppliers", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
      void qc.invalidateQueries({ queryKey: ["supplier"] });
      void qc.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}
